import { baseApi } from "./baseApi";

export interface AdminProfile {
  id: string;
  email: string;
  name: string;
  phone?: string | null;
  role: "ADMIN" | "STAFF" | "SUPER_ADMIN";
  avatarUrl?: string | null;
  isSuspended: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateProfileData {
  name?: string;
  phone?: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

/**
 * Profile API slice
 * Handles admin profile management API calls
 */
export const profileApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get admin profile
    getAdminProfile: builder.query<
      { success: boolean; data: AdminProfile; message?: string },
      void
    >({
      query: () => ({
        url: "/profile",
        credentials: "include",
      }),
      providesTags: ["User"],
    }),

    // Update admin profile
    updateAdminProfile: builder.mutation<
      { success: boolean; data: AdminProfile; message?: string },
      UpdateProfileData
    >({
      query: (data) => ({
        url: "/profile",
        method: "PUT",
        body: data,
        credentials: "include",
      }),
      invalidatesTags: ["User"],
    }),

    // Change password
    changeAdminPassword: builder.mutation<
      { success: boolean; message?: string },
      ChangePasswordData
    >({
      query: (data) => ({
        url: "/profile/change-password",
        method: "POST",
        body: data,
        credentials: "include",
      }),
    }),

    // Upload avatar (admin only)
    uploadAvatar: builder.mutation<
      { success: boolean; data: AdminProfile; message?: string },
      File
    >({
      query: (file) => {
        const formData = new FormData();
        formData.append("avatar", file);
        return {
          url: "/profile/avatar",
          method: "POST",
          body: formData,
          credentials: "include",
        };
      },
      invalidatesTags: ["User"],
    }),

    // Remove avatar (admin only)
    removeAvatar: builder.mutation<
      { success: boolean; data: AdminProfile; message?: string },
      void
    >({
      query: () => ({
        url: "/profile/avatar",
        method: "DELETE",
        credentials: "include",
      }),
      invalidatesTags: ["User"],
    }),
  }),
});

export const {
  useGetAdminProfileQuery,
  useUpdateAdminProfileMutation,
  useChangeAdminPasswordMutation,
  useUploadAvatarMutation,
  useRemoveAvatarMutation,
} = profileApi;
